import React, { useEffect, useMemo, useRef, useState } from "react";
import DataGrid, { Column, Editing, Popup, Paging, Form, NumericRule, CustomRule } from "devextreme-react/data-grid";
import { RequiredRule } from "devextreme-react/validator";
import { Item } from "devextreme-react/form";

import { Role, Status, TitleDetail, Title, User, TicketInformation } from "./interface";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  IdcardFilled,
  FolderFilled,
  ClockCircleOutlined,
  FlagOutlined,
  NotificationOutlined,
} from "@ant-design/icons";

import { DataGrid as DxReactDataGrid } from "devextreme-react/data-grid";
import "./WorkTicket.scss";
import { BsPlusLg } from "react-icons/bs";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import {
  createTicket,
  deleteTicket,
  findAllRole,
  findAllStatus,
  findAllTitle,
  findAllTitleDetail,
  findTicketByUserId,
  findTicketOfUserByTaskId,
  updateTicket,
} from "./api";
import { NHANVIENID, NICKNAME} from "authProvider";
import { getListUsers } from "method";
import { withPermission } from "utils/withPermission";
import { formatEstimateHours } from "utils/EstimatedHourFormat";
import { PermissionEnum } from "enums/permission.enum";

const WorkTicket: React.FC<{ currentTaskDetailId?: number }> = ({ currentTaskDetailId }) => {
  const dataGridRef = useRef<DxReactDataGrid>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [titles, setTitles] = useState<Title[]>([]);
  const [titleDetails, setTitleDetails] = useState<TitleDetail[]>([]);

  const [groupedTitleDataSource, setGroupedTitleDataSource] = useState<DataSource | null>(null);

  const currentNickname = String(localStorage.getItem(NICKNAME));
  const currentNhanVien = Number(localStorage.getItem(NHANVIENID));
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const listUser = await getListUsers();
        if (listUser && Array.isArray(listUser.data)) {
          setUsers(listUser.data);
        } else {
          console.error("getListUsers did not return expected structure:", listUser);
          setUsers([]);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setUsers([]);
      }
    };
    fetchUsers();
  }, [currentNhanVien]);

  const TicketInformationStore = useMemo(
    () =>
      new CustomStore({
        key: "id",
        load: async () => {
          try {
            if (!currentTaskDetailId) {
              return [];
            }
            const allTicketInformationsByUserId = await findTicketOfUserByTaskId(currentNhanVien, currentTaskDetailId);
            return allTicketInformationsByUserId
          } catch (error) {
            console.error("Failed to load ticket", error);
            throw error;
          }
        },
        insert: async (data) => {
          try {
            if (currentTaskDetailId) {
              data.taskId = currentTaskDetailId;
            }
            const createTicketInformations = await createTicket(data);
            return createTicketInformations;
          } catch (error) {
            console.error("Failed to create ticket", error);
            throw error;
          }
        },
        update: async (id, data) => {
          try {
            await updateTicket(id, data);
          } catch (error) {
            console.error("Failed to update ticket", error);
            throw error;
          }
        },

        remove: async (id) => {
          try {
            await deleteTicket(id);
          } catch (error) {
            console.error("Failed to delete ticket", error);
            throw error;
          }
        },
      }),
    [currentNhanVien, currentTaskDetailId]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allRoles = await findAllRole();
        const allStatuses = await findAllStatus();
        const allTitles = await findAllTitle();
        const allTitleDetails = await findAllTitleDetail();

        setRoles(allRoles);
        setStatuses(allStatuses);
        setTitles(allTitles);
        setTitleDetails(allTitleDetails);

        const groupedData = allTitleDetails.map((detail: any) => {
          const title = allTitles.find((t: any) => t.id === detail.titleId);
          return {
            ...detail,
            titleName: title?.titleName,
          };
        });

        const dataSource = new DataSource({
          store: {
            type: "array",
            data: groupedData,
            key: "id",
          },
          group: "titleName",
          sort: [{ selector: "titleId", desc: false }],
        });
        setGroupedTitleDataSource(dataSource);
      } catch (error) {
        console.error("Failed to fetch data", error);
      }
    };
    fetchData();
  }, []);

  const handleAddRow = () => {
    if (!currentTaskDetailId) {
      return;
    }
    dataGridRef.current?.instance?.addRow();
  };

  return (
    <div className="data-grid-demo">
      <p>Work Ticket</p>
      <DataGrid
        ref={dataGridRef}
        dataSource={TicketInformationStore}
        showColumnHeaders={false}
        onEditingStart={() => setIsEditing(true)}
        onInitNewRow={(e) => {
          setIsEditing(false);
          e.data.userId = currentNhanVien;
          e.data.statusId = 1;
          if (currentTaskDetailId) {
            e.data.taskId = currentTaskDetailId;
          }
        }}
        noDataText={!currentTaskDetailId ? "Please select a task first" : "No tickets available"}
      >
        <Paging enabled={false} />
        <Editing mode="popup" allowUpdating allowDeleting useIcons={false}>
          <Popup
            title={isEditing ? "Update Ticket" : "Add Ticket"}
            showTitle
            width={700}
            height={525}
            showCloseButton
            toolbarItems={[
              ...(isEditing
                ? [
                    {
                      toolbar: "bottom",
                      location: "after",
                      widget: "dxButton",
                      options: {
                        text: "Cancel",
                        onClick: () => {
                          dataGridRef.current?.instance?.cancelEditData();
                        },
                      },
                    },
                  ]
                : []),
              {
                toolbar: "bottom",
                location: "after",
                widget: "dxButton",
                options: {
                  text: isEditing ? "Save Changes" : "Add Ticket",
                  type: "default",
                  stylingMode: "contained",
                  elementAttr: {
                    class: "custom-green-button",
                  },
                  onClick: () => {
                    dataGridRef.current?.instance?.saveEditData();
                  },
                },
              },
            ]}
          />

          <Form labelLocation="top" colCount={2}>
            <Item
              dataField="userId"
              editorType="dxSelectBox"
              editorOptions={{
                items: [{ id: currentNhanVien, name: currentNickname }],
                valueExpr: "id",
                displayExpr: "name",
                width: "100%",
                value: currentNhanVien,
                showDropDownButton: false,
              }}
              disabled={true}
            />

            <Item
              dataField="roleId"
              editorType="dxSelectBox"
              colSpan={2}
              editorOptions={{
                dataSource: roles,
                valueExpr: "id",
                width: "100%",
                displayExpr: "roleName",
                placeholder: "Select Role",
              }}
            >
              <RequiredRule message="Role is required" />
            </Item>
            <Item
              dataField="titleDetailId"
              colSpan={2}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: groupedTitleDataSource,
                valueExpr: "id",
                displayExpr: "titleDetailName",
                width : "100%",
                grouped: true,
                searchEnabled: true,
                placeholder: "Select title detail",
              }}
            >
              <RequiredRule message="Detail is required" />
            </Item>

            <Item
              dataField="customTitle"
              colSpan={2}
              editorOptions={{
                placeholder: "Enter custom title (optional)",
              }}
            ></Item>

            <Item
              dataField="estimateHours"
              colSpan={2}
              editorType="dxTextBox"
              editorOptions={{
                placeholder: "Hours",
              }}
            >
              <RequiredRule message="Estimate is required" />
            </Item>
            <Item
              dataField="statusId"
              colSpan={2}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: statuses,
                valueExpr: "id",
                width : "100%",
                value: 1,
                displayExpr: "statusName",
                showDropDownButton: false,
              }}
              disabled={!isEditing}
            />
          </Form>
        </Editing>
        <Column
          dataField="userId"
          caption="User Name"
          cellRender={() => {
            return (
              <span>
                {" "}
                <UserOutlined style={{ marginRight: 6 }} />
                {currentNickname|| "Unknown User"}
              </span>
            );
          }}
        />
        <Column
          dataField="roleId"
          caption="Role"
          cellRender={(e) => {
            const role = roles.find((r) => r.id === e.value);
            return (
              <span>
                <IdcardFilled style={{ marginRight: 6 }} />
                {role?.roleName}
              </span>
            );
          }}
        />

        <Column
          dataField="titleDetailId"
          caption="Title Detail"
          cellRender={(e) => {
            const title = titleDetails.find((t: any) => t.id === e.value);
            return (
              <span>
                <FolderFilled style={{ marginRight: 6 }} />
                {title?.titleDetailName}
              </span>
            );
          }}
        />

        <Column
          dataField="estimateHours"
          caption="Estimate (hours)"
          cellRender={(e) => {
            const formatted = formatEstimateHours(e.value);
            return (
              <span>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  {formatted}
              </span>
          );
          }}
        />
        <Column
          dataField="statusId"
          caption="Status"
          cellRender={(e) => {
            const status = statuses.find((s: any) => s.id === e.value);
            return (
              <span>
                <FlagOutlined style={{ marginRight: 6 }} />
                {status?.statusName}
              </span>
            );
          }}
        />
        <Column
          dataField="notifyTo"
          visible={false}
          caption="Notify"
          cellRender={(e) => {
            const names = (e.value || [])
              .map((id: any) => users.find((u: any) => u.userId === id)?.name)
              .filter(Boolean)
              .join(", ");
            return (
              <span>
                <NotificationOutlined style={{ marginRight: 6 }} />
                {names}
              </span>
            );
          }}
        />
        <Column
          type="buttons"
          width={100}
          cellRender={(e) => (
            <div style={{ display: "flex", gap: "10px" }}>
              <EyeOutlined style={{ cursor: "pointer" }} />
              <EditOutlined style={{ cursor: "pointer" }} onClick={() => e.component.editRow(e.rowIndex)} />
              <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => e.component.deleteRow(e.rowIndex)} />
            </div>
          )}
        />
      </DataGrid>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-start" }}>
        <button className="add-ticket-button" onClick={handleAddRow} disabled={!currentTaskDetailId}>
          <BsPlusLg />

          
          <span>Add Ticket</span>


        </button>
      </div>
    </div>
  );
};

export default withPermission([PermissionEnum.CNTT])(WorkTicket) as React.FC<{ currentTaskDetailId?: number }>;
